import React, { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { Checkbox, Col, Row, Select } from 'antd';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import MyModal from 'components/MyModal';
import { messagingService } from 'v2/services/messaging';

import { useChatAffiliates } from '../../useChatAffiliates';
import { useChatProducts } from '../../useChatProducts';

const filterSelectOption = (input: string, option?: { label?: unknown; value?: unknown }) => {
  const label = String(option?.label ?? '').toLowerCase();
  const value = String(option?.value ?? '').toLowerCase();
  const term = input.toLowerCase();

  return label.includes(term) || value.includes(term);
};

const filterAffiliateOption = (input: string, option?: { label?: unknown }) => {
  const label = String(option?.label ?? '').toLowerCase();
  const term = input.toLowerCase();

  return label.includes(term);
};

interface IUniqueAffiliate {
  id: number;
  name: string;
  isApproved: boolean;
  isPending: boolean;
}

interface IProps {
  closeModal: () => void;
  active: boolean;
}

const ModalNewChat: React.FC<IProps> = ({ closeModal }) => {
  const [productsNew, setProductsNew] = useState<number[]>([]);
  const [affiliatesNew, setAffiliatesNew] = useState<number[]>([]);
  const [affiliatesSelectKey, setAffiliatesSelectKey] = useState(0);
  const [affiliatesSelectOpen, setAffiliatesSelectOpen] = useState(false);
  const [valueAnswer, setValueAnswer] = useState('');
  const [sendEmail, setSendEmail] = useState(false);

  const { products, isFetching: isFetchingProducts } = useChatProducts();
  const { affiliates, isFetching: isFetchingAffiliates } = useChatAffiliates(productsNew);

  const queryClient = useQueryClient();
  const newMessageMutation = useMutation({
    mutationFn: async () => {
      return messagingService.sendMessageBroadcast(valueAnswer, affiliatesNew, sendEmail);
    },
    onSuccess: () => {
      toast.success('Mensagem enviada com sucesso');
      queryClient.invalidateQueries({ queryKey: ['messages', 'chats'] });
      closeModal();
    },
    onError: (error: any) => {
      toast.error(error);
    }
  });

  const handleClickNew = useCallback(async () => {
    if (productsNew.length === 0 || affiliatesNew.length === 0) {
      toast.info('Selecione ao menos um produto e um afiliado');
      return;
    }

    if (!valueAnswer) {
      toast.info('Escreva sua mensagem');
      return;
    }

    await newMessageMutation.mutateAsync();
  }, [affiliatesNew, newMessageMutation, productsNew, valueAnswer]);

  const onChangeProducts = useCallback(
    (value: number[]) => {
      setProductsNew(value);
    },
    [setProductsNew]
  );

  const onChangeAffiliates = useCallback((value: number[]) => {
    setAffiliatesNew(value);
  }, []);

  const resetAffiliatesSelect = useCallback(() => {
    setAffiliatesSelectOpen(false);
    setAffiliatesSelectKey(key => key + 1);
  }, []);

  const uniqueAffiliates = useMemo(() => {
    const byId = new Map<number, IUniqueAffiliate>();

    affiliates.forEach(affiliate => {
      const current = byId.get(affiliate.id);

      if (!current) {
        byId.set(affiliate.id, {
          id: affiliate.id,
          name: affiliate.name,
          isApproved: affiliate.status === 'A',
          isPending: affiliate.status === 'P'
        });
        return;
      }

      if (affiliate.status === 'A') {
        current.isApproved = true;
      }

      if (affiliate.status === 'P') {
        current.isPending = true;
      }
    });

    return Array.from(byId.values());
  }, [affiliates]);

  const affiliateOptions = useMemo(
    () => uniqueAffiliates.map(affiliate => ({ value: affiliate.id, label: affiliate.name })),
    [uniqueAffiliates]
  );

  const selectAffiliates = useCallback(
    (status: string) => {
      if (productsNew.length === 0) {
        toast.info('Selecione um ou mais produtos para liberar essa função');
        return;
      }

      if (uniqueAffiliates.length === 0) {
        toast.info('Nenhum afiliado/coprodutor encontrado');
        return;
      }

      let selectedAffiliates: IUniqueAffiliate[] = [];

      switch (status) {
        case 'approved':
          selectedAffiliates = uniqueAffiliates.filter(affiliate => affiliate.isApproved);
          break;
        case 'pending':
          selectedAffiliates = uniqueAffiliates.filter(affiliate => affiliate.isPending);
          break;
        case 'none':
          selectedAffiliates = [];
          break;
        default:
          selectedAffiliates = uniqueAffiliates;
          break;
      }

      setAffiliatesNew(selectedAffiliates.map(affiliate => affiliate.id));
      resetAffiliatesSelect();
    },
    [uniqueAffiliates, productsNew, resetAffiliatesSelect]
  );

  const selectAllProducts = useCallback(() => {
    setProductsNew(products.map(({ id }) => id));
  }, [products, setProductsNew]);

  const deSelectAllProducts = useCallback(() => {
    setProductsNew([]);
    setAffiliatesNew([]);
  }, []);

  const handleChangeFieldAnswer = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValueAnswer(e.target.value);
  }, []);

  const handleChangeSendEmail = useCallback((e: any) => {
    setSendEmail(e.target.checked);
  }, []);

  return (
    <MyModal
      hasActive={true}
      title={'Nova mensagem'}
      closeModal={closeModal}
      textButtonOk={'Enviar'}
      hasButtons={true}
      handleClickOk={handleClickNew}
      loading={newMessageMutation.isPending}
    >
      <div>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <label className='mb-1 block text-sm'>Produtos</label>
            <Select
              mode='multiple'
              showSearch
              optionFilterProp='label'
              filterOption={filterSelectOption}
              className='w-full'
              value={productsNew}
              onChange={onChangeProducts}
              options={products.map(product => ({ value: product.id, label: product.name }))}
              maxTagCount={5}
              maxTagPlaceholder={omittedValues => `+ ${omittedValues.length} produtos`}
            />
            <div className='flex w-full justify-end'>
              <div
                className='hover:text-primary mx-1 inline-block cursor-pointer text-[13px]'
                onClick={selectAllProducts}
              >
                Selecionar todos produtos
              </div>
              {productsNew && productsNew.length > 0 && (
                <div
                  className='hover:text-primary mx-1 inline-block cursor-pointer text-[13px]'
                  onClick={deSelectAllProducts}
                >
                  Remover todos produtos
                </div>
              )}
            </div>
          </Col>
          {productsNew.length > 0 && uniqueAffiliates.length > 0 ? (
            <Col span={24}>
              <label className='mb-1 block text-sm'>Afiliados</label>
              <Select
                key={affiliatesSelectKey}
                mode='multiple'
                showSearch
                optionFilterProp='label'
                filterOption={filterAffiliateOption}
                className='w-full'
                open={affiliatesSelectOpen}
                onOpenChange={setAffiliatesSelectOpen}
                value={affiliatesNew}
                onChange={onChangeAffiliates}
                options={affiliateOptions}
                maxTagCount={5}
                maxTagPlaceholder={omittedValues => `+ ${omittedValues.length} afiliados`}
              />
              {productsNew && productsNew.length > 0 && (
                <div className='flex w-full justify-end'>
                  <div
                    className='hover:text-primary mx-1 inline-block cursor-pointer text-[13px]'
                    onClick={() => selectAffiliates('approved')}
                  >
                    Selecionar aprovados
                  </div>
                  <div
                    className='hover:text-primary mx-1 inline-block cursor-pointer text-[13px]'
                    onClick={() => selectAffiliates('pending')}
                  >
                    Selecionar pendentes
                  </div>
                  <div
                    className='hover:text-primary mx-1 inline-block cursor-pointer text-[13px]'
                    onClick={() => selectAffiliates('all')}
                  >
                    Selecionar todos
                  </div>
                  {affiliatesNew && affiliatesNew.length > 0 && (
                    <div
                      className='hover:text-primary mx-1 inline-block cursor-pointer text-[13px]'
                      onClick={() => selectAffiliates('none')}
                    >
                      Remover todos afiliados
                    </div>
                  )}
                </div>
              )}
            </Col>
          ) : isFetchingAffiliates || isFetchingProducts ? (
            <Col span={24}>
              <span>Carregando afiliados...</span>
            </Col>
          ) : productsNew.length > 0 ? (
            <Col span={24}>
              <span>Não foi encontrado nenhum afiliado para o(s) produto(s) selecionado(s).</span>
            </Col>
          ) : (
            <Col span={24}>
              <span>Selecione pelo menos 1 produto para carregar os afiliados</span>
            </Col>
          )}
          <Col span={24}>
            <label>Mensagem</label>
            <textarea
              className='min-h-[60px] w-full resize-none rounded-sm border border-[#ced4da] px-3 py-[10px] text-[15px] leading-[19px] font-normal text-[#596375] transition-all duration-300 focus:border-[#a7a7a7]'
              placeholder='Escreva sua mensagem...'
              onChange={handleChangeFieldAnswer}
              value={valueAnswer}
              disabled={
                isFetchingProducts || isFetchingAffiliates || productsNew.length === 0 || affiliatesNew.length === 0
              }
            ></textarea>
            <Checkbox checked={sendEmail} onChange={handleChangeSendEmail} />{' '}
            <span style={{ fontSize: 13 }}>Enviar cópia por e-mail</span>
          </Col>
        </Row>
      </div>
    </MyModal>
  );
};

export default ModalNewChat;
